/**
 * The Academic Job Cover Letter Generator: a faculty or postdoctoral application letter
 * assembled from the applicant's own account of their research, teaching and fit.
 *
 * The applicant writes the substance. The template supplies the frame — the opening, the
 * link between paragraphs, the enclosure list and the close — in the US or the UK
 * convention, and measures the result against the length each convention expects.
 * Frame syntax is documented in `correspondence/compose.ts`.
 *
 * ## US and UK conventions
 *
 * US (and Canadian) faculty letters usually run one and a half to two pages and are read
 * alongside separate research and teaching statements. UK academic letters are typically
 * no more than two pages of A4 and often answer a person specification; the UK form
 * writes "programme" and "Yours sincerely", and dates as "5 October 2026". These are
 * conventions, not rules: a posting's own instructions always win.
 */

import { t } from "@/lib/i18n/interpolate";
import {
  asFragment,
  asSentence,
  cleanBlock,
  cleanLine,
  compose,
  countWords,
  joinList,
  orPlaceholder,
  splitItems,
} from "@/lib/tool-kit/correspondence/compose";

export const JOB_REGIONS = ["us", "uk"] as const;
export type JobRegion = (typeof JOB_REGIONS)[number];

export const JOB_KINDS = ["faculty", "postdoc"] as const;
export type JobKind = (typeof JOB_KINDS)[number];

export const ENCLOSURES = [
  "cv",
  "researchStatement",
  "teachingStatement",
  "writingSample",
  "supportingStatement",
] as const;
export type Enclosure = (typeof ENCLOSURES)[number];

/** A page of single-spaced letter text, once the date, address and signature are set. */
export const WORDS_PER_PAGE = 450;
/** Two pages: the ceiling both conventions share. */
export const MAX_PAGES = 2;

export interface JobLetterInput {
  /** Already formatted for the reader's locale, or "" for no date line. */
  date: string;
  region: JobRegion;
  kind: JobKind;
  recipient: string;
  position: string;
  department: string;
  institution: string;
  reference: string;
  status: string;
  research: string;
  plan: string;
  teaching: string;
  courses: string;
  newCourse: string;
  fit: string;
  collaborators: string;
  service: string;
  enclosed: Record<Enclosure, boolean>;
  referees: boolean;
  signName: string;
  signTitle: string;
  email: string;
}

export const JOB_LETTER_BANK = {
  greetings: {
    us: "Dear Members of the Search Committee,",
    uk: "Dear Sir or Madam,",
    named: "Dear {recipient},",
  },
  signOffs: {
    us: "Sincerely,",
    uk: "Yours sincerely,",
    ukAnonymous: "Yours faithfully,",
  },
  enclosures: {
    cv: "curriculum vitae",
    researchStatement: "research statement",
    teachingStatement: "teaching statement",
    writingSample: "writing sample",
    supportingStatement: "supporting statement",
  },
  placeholders: {
    position: "Position title",
    department: "Department",
    institution: "Institution",
    research: "Your research: what you study, what you found, why it matters",
    teaching: "Your teaching: courses, approach, and evidence it works",
    fit: "Why this department: specific colleagues, centres or students",
    signName: "Your name",
    signTitle: "Your current position",
  },
  referenceClause: " (reference {reference})",
  enclosedOne: "Enclosed is my {list}.",
  enclosedMany: "Enclosed are my {list}.",
  opening: [
    "?faculty:I am writing to apply for the position of {position} in {department} at {institution}{reference}.",
    "?postdoc:I am writing to apply for the postdoctoral position in {department} at {institution}{reference}.",
    "?status:I am {status}.",
  ],
  research: ["{research}", "?plan:At {institution}, I plan to {plan}."],
  teaching: [
    "{teaching}",
    "?courses:I am prepared to teach {courses}.",
    "?newCourse:I would also be glad to develop a course on {newCourse}.",
  ],
  fit: [
    "{fit}",
    "?collaborators:I would welcome the chance to collaborate with {collaborators}.",
  ],
  service: ["{service}"],
  closing: [
    "?referees?us:Letters from my referees will be sent separately.",
    "?referees?uk:My referees are named on my application form.",
    "?email:I would welcome the opportunity to discuss my application further and can be reached at {email}.",
    "!email:I would welcome the opportunity to discuss my application further.",
    "?us:Thank you for your time and consideration.",
    "?uk:Thank you for considering my application.",
  ],
} as const;

export interface JobLetterBank {
  greetings: Readonly<Record<"us" | "uk" | "named", string>>;
  signOffs: Readonly<Record<"us" | "uk" | "ukAnonymous", string>>;
  enclosures: Readonly<Record<Enclosure, string>>;
  placeholders: Readonly<Record<string, string>>;
  referenceClause: string;
  enclosedOne: string;
  enclosedMany: string;
  opening: readonly string[];
  research: readonly string[];
  teaching: readonly string[];
  fit: readonly string[];
  service: readonly string[];
  closing: readonly string[];
}

const UNIT_WORD =
  /\b(?:department|faculty|school|division|college|institute|centre|center|laboratory)\b/i;
const STARTS_WITH_THE = /^the\s/i;

/** "Department of Biology" reads "in the Department of Biology"; "Biology" is left alone. */
export function withUnitArticle(name: string): string {
  if (name === "" || STARTS_WITH_THE.test(name) || !UNIT_WORD.test(name)) {
    return name;
  }
  return `the ${name}`;
}

const LIST_LOCALES: Record<JobRegion, string> = { us: "en-US", uk: "en-GB" };

function isEnglish(locale: string): boolean {
  return locale === "en" || locale.startsWith("en-");
}

/**
 * The locale lists are joined in: the region's English for an English letter, otherwise
 * the reader's own language, so a Dutch letter reads "A, B en C", not "A, B, and C".
 */
function listLocale(region: JobRegion, locale: string): string {
  return isEnglish(locale) ? LIST_LOCALES[region] : locale;
}

function greeting(input: JobLetterInput, bank: JobLetterBank): string {
  const recipient = cleanLine(input.recipient);
  return recipient === ""
    ? bank.greetings[input.region]
    : t(bank.greetings.named, { recipient });
}

function signOff(input: JobLetterInput, bank: JobLetterBank): string {
  if (input.region === "us") {
    return bank.signOffs.us;
  }
  return cleanLine(input.recipient) === ""
    ? bank.signOffs.ukAnonymous
    : bank.signOffs.uk;
}

function enclosureSentence(
  input: JobLetterInput,
  bank: JobLetterBank,
  locale: string
): string {
  const chosen = ENCLOSURES.filter((key) => input.enclosed[key]).map(
    (key) => bank.enclosures[key]
  );
  if (chosen.length === 0) {
    return "";
  }
  const list = joinList(chosen, listLocale(input.region, locale));
  return t(chosen.length === 1 ? bank.enclosedOne : bank.enclosedMany, {
    list,
  });
}

/**
 * The letter as marked paragraphs: date and greeting first, signature last. `locale` is
 * the page language; English adds "the" before a unit name and joins lists by region.
 */
export function buildJobLetter(
  input: JobLetterInput,
  bank: JobLetterBank,
  locale = "en"
): string[] {
  const lists = listLocale(input.region, locale);
  const department = asFragment(input.department);
  const ph = (key: string) => bank.placeholders[key] ?? key;
  const reference = cleanLine(input.reference);
  const email = cleanLine(input.email);
  const courses = splitItems(input.courses, { commas: true });
  const teachingText = asSentence(input.teaching);
  const teachingOn = input.kind === "faculty" || teachingText !== "";
  const values: Record<string, string> = {
    position: orPlaceholder(asFragment(input.position), ph("position")),
    department: orPlaceholder(
      isEnglish(locale) ? withUnitArticle(department) : department,
      ph("department")
    ),
    institution: orPlaceholder(
      asFragment(input.institution),
      ph("institution")
    ),
    reference: reference === "" ? "" : t(bank.referenceClause, { reference }),
    status: asFragment(input.status),
    research: orPlaceholder(asSentence(input.research), ph("research")),
    plan: asFragment(input.plan),
    teaching: orPlaceholder(teachingText, ph("teaching")),
    courses: joinList(courses, lists),
    newCourse: asFragment(input.newCourse),
    fit: orPlaceholder(asSentence(input.fit), ph("fit")),
    collaborators: joinList(
      splitItems(input.collaborators, { commas: true }),
      lists
    ),
    service: asSentence(cleanBlock(input.service)),
    email,
  };
  const flags = new Set<string>([input.region, input.kind]);
  const set = (flag: string, on: boolean) => {
    if (on) {
      flags.add(flag);
    }
  };
  set("status", values.status !== "");
  set("plan", values.plan !== "");
  set("courses", courses.length > 0);
  set("newCourse", values.newCourse !== "");
  set("collaborators", values.collaborators !== "");
  set("referees", input.referees);
  set("email", email !== "");

  const paragraphs: string[] = [];
  const date = cleanLine(input.date);
  if (date !== "") {
    paragraphs.push(date);
  }
  paragraphs.push(greeting(input, bank));
  paragraphs.push(...compose(bank.opening, values, flags));
  paragraphs.push(...compose(bank.research, values, flags));
  if (teachingOn) {
    paragraphs.push(...compose(bank.teaching, values, flags));
  }
  paragraphs.push(...compose(bank.fit, values, flags));
  if (values.service !== "") {
    paragraphs.push(...compose(bank.service, values, flags));
  }
  const enclosed = enclosureSentence(input, bank, locale);
  const closing = compose(bank.closing, values, flags).join(" ");
  paragraphs.push([enclosed, closing].filter((part) => part !== "").join(" "));
  paragraphs.push(
    [
      signOff(input, bank),
      orPlaceholder(cleanLine(input.signName), ph("signName")),
      orPlaceholder(cleanLine(input.signTitle), ph("signTitle")),
    ].join("\n")
  );
  return paragraphs;
}

export type LengthStatus = "short" | "ok" | "long";

export interface LengthCheck {
  words: number;
  pages: number;
  status: LengthStatus;
}

/**
 * Words and an estimated page count (450 words to a page), against the one-to-two-page
 * range both conventions use. An estimate: type size, margins and the address block
 * change it, so the page count of the finished document is what matters.
 */
export function checkJobLetterLength(
  paragraphs: readonly string[]
): LengthCheck {
  const words = countWords(paragraphs.join("\n"));
  const pages = Math.round((words / WORDS_PER_PAGE) * 10) / 10;
  let status: LengthStatus = "ok";
  if (words < WORDS_PER_PAGE) {
    status = "short";
  } else if (words > WORDS_PER_PAGE * MAX_PAGES) {
    status = "long";
  }
  return { words, pages, status };
}
